import React, { useEffect, useMemo, useState, useCallback } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Sparkle, Trash, DotsSixVertical, Cpu, GearSix, PencilSimple, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";
import { jsPDF } from "jspdf";

function AthleteCard({ a }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: a.id });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 } : undefined;
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}
      className={`flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-secondary text-sm cursor-grab active:cursor-grabbing select-none ${isDragging ? "opacity-60" : ""}`}
      data-testid={`athlete-chip-${a.id}`}>
      <DotsSixVertical size={14} className="text-muted-foreground shrink-0" />
      <span className="font-medium truncate">{a.last_name} {a.first_name}</span>
      <span className="ml-auto text-xs text-muted-foreground shrink-0">{a.belt || ""} {a.weight ? `· ${a.weight}kg` : ""} {a.birth_date ? `· ${a.birth_date.slice(0, 4)}` : ""}</span>
    </div>
  );
}

function Column({ id, title, subtitle, children, headerRight, testid }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} data-testid={testid}
      className={`flex flex-col rounded-lg border bg-card min-h-[220px] transition-colors ${isOver ? "border-gold bg-gold/5" : "border-border"}`}>
      <div className="flex items-center justify-between p-3 border-b border-border">
        <div className="min-w-0">
          <div className="font-semibold truncate">{title}</div>
          {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
        </div>
        {headerRight}
      </div>
      <div className="p-3 space-y-2 flex-1">{children}</div>
    </div>
  );
}

export default function PoolsTab({ eventId, event }) {
  const t = useT();
  const canEdit = ["owner", "co_organizer"].includes(event.my_role);
  const disciplines = event.type === "mixed" ? ["combat", "poomsae"] : [event.type];
  const [discipline, setDiscipline] = useState(disciplines[0]);
  const [athletes, setAthletes] = useState([]);
  const [pools, setPools] = useState([]);
  const [matchCounts, setMatchCounts] = useState({});
  const [matches, setMatches] = useState([]);
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoCfg, setAutoCfg] = useState({ method: "ai", min_size: 3, max_size: 8, weight_tolerance_pct: 10, age_tolerance_pct: 100, height_tolerance_pct: 100, belt_tolerance_pct: 100, mixed_pools: false, replace: true });
  const [overridePool, setOverridePool] = useState(null);
  const [overrideCfg, setOverrideCfg] = useState({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    try {
      const [aRes, pRes, mRes] = await Promise.all([
        api.get(`/events/${eventId}/athletes`),
        api.get(`/events/${eventId}/pools`),
        api.get(`/events/${eventId}/matches`),
      ]);
      setAthletes(aRes.data);
      setPools(pRes.data);
      setMatches(mRes.data);
      const counts = {};
      mRes.data.forEach((m) => { counts[m.pool_id] = (counts[m.pool_id] || 0) + 1; });
      setMatchCounts(counts);
    } catch (e) {
      // transient backend reload / network hiccup — keep last known state, no crash overlay
      console.debug("PoolsTab load skipped:", e?.message);
    }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const dPools = pools.filter((p) => p.discipline === discipline);
  const assignedIds = useMemo(() => new Set(dPools.flatMap((p) => p.athlete_ids)), [dPools]);
  const pool_of = athletes.filter((a) => a.disciplines?.includes(discipline));
  const unassigned = pool_of.filter((a) => !assignedIds.has(a.id));
  const byId = useMemo(() => Object.fromEntries(athletes.map((a) => [a.id, a])), [athletes]);

  const persist = async (pool, athlete_ids) => {
    const body = { name: pool.name, discipline: pool.discipline, bracket_type: pool.bracket_type, athlete_ids, criteria: pool.criteria, config_override: pool.config_override };
    await api.put(`/pools/${pool.id}`, body);
  };

  const onDragEnd = async ({ active, over }) => {
    if (!canEdit) return;
    if (!over) return;
    const athleteId = active.id;
    const target = over.id; // 'unassigned' or pool id
    const source = dPools.find((p) => p.athlete_ids.includes(athleteId));
    const sourceId = source ? source.id : "unassigned";
    if (sourceId === target) return;

    const next = pools.map((p) => {
      if (p.id === sourceId) return { ...p, athlete_ids: p.athlete_ids.filter((x) => x !== athleteId) };
      if (p.id === target) return { ...p, athlete_ids: [...p.athlete_ids, athleteId] };
      return p;
    });
    setPools(next);
    try {
      if (source) await persist(source, source.athlete_ids.filter((x) => x !== athleteId));
      if (target !== "unassigned") {
        const tp = pools.find((p) => p.id === target);
        await persist(tp, [...tp.athlete_ids, athleteId]);
      }
    } catch (e) {
      toast.error(t("pool.moveError"));
      load();
    }  };

  const createPool = async () => {
    const bracket = discipline === "poomsae" ? "score" : "single_elim";
    await api.post(`/events/${eventId}/pools`, { name: `Pool ${dPools.length + 1}`, discipline, bracket_type: bracket, athlete_ids: [], criteria: "" });
    load();
  };

  const runAutoGenerate = async () => {
    setAutoLoading(true);
    try {
      const body = {
        discipline,
        mode: autoCfg.method,
        min_size: Number(autoCfg.min_size),
        max_size: Number(autoCfg.max_size),
        weight_tolerance_pct: Number(autoCfg.weight_tolerance_pct),
        age_tolerance_pct: Number(autoCfg.age_tolerance_pct),
        height_tolerance_pct: Number(autoCfg.height_tolerance_pct),
        belt_tolerance_pct: Number(autoCfg.belt_tolerance_pct),
        mixed_pools: autoCfg.mixed_pools,
        replace: autoCfg.replace,
      };
      const { data } = await api.post(`/events/${eventId}/pools/auto-generate`, body);
      if (autoCfg.method === "ai" && !data.ai_used) {
        toast.warning(t("pool.aiFallback", { n: data.count }));
      } else {
        toast.success(t("pool.autoCreated", { n: data.count }));
      }
      setAutoOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAutoLoading(false);
    }
  };

  const openOverride = (pool) => {
    const base = pool.discipline === "combat" ? event.combat_settings : event.poomsae_settings;
    setOverrideCfg({ ...(base || {}), ...(pool.config_override || {}) });
    setOverridePool(pool);
  };

  const saveOverride = async (reset = false) => {
    await persist({ ...overridePool, config_override: reset ? null : overrideCfg }, overridePool.athlete_ids);
    toast.success(t("pool.overrideSaved"));
    setOverridePool(null);
    load();
  };

  const ovNum = (k, label) => (
    <div className="space-y-1.5" key={k}>
      <Label className="text-xs">{label}</Label>
      <Input type="number" min={1} value={overrideCfg[k] ?? ""} data-testid={`override-${k}`}
        onChange={(e) => setOverrideCfg((c) => ({ ...c, [k]: parseInt(e.target.value) || 0 }))} />
    </div>
  );

  const ovFloat = (k, label, { min = 0.2, max = 2.0, step = 0.1 } = {}) => (
    <div className="space-y-1.5" key={k}>
      <Label className="text-xs">{label}</Label>
      <Input type="number" min={min} max={max} step={step} value={overrideCfg[k] ?? ""} data-testid={`override-${k}`}
        onChange={(e) => { const v = parseFloat(e.target.value); setOverrideCfg((c) => ({ ...c, [k]: isNaN(v) ? min : Math.max(min, Math.min(max, v)) })); }} />
    </div>
  );

  const renamePool = async (pool) => {
    const name = window.prompt(t("pool.renamePrompt"), pool.name);
    if (name == null || !name.trim()) return;
    await persist({ ...pool, name: name.trim() }, pool.athlete_ids);
    load();
  };

  const setBracket = async (pool, v) => {
    await persist({ ...pool, bracket_type: v }, pool.athlete_ids);
    load();
  };

  const genMatches = async (pool) => {
    if (pool.athlete_ids.length < (pool.discipline === "poomsae" ? 1 : 2)) return toast.error(t("pool.needMore"));
    const { data } = await api.post(`/pools/${pool.id}/generate-matches`);
    toast.success(t("pool.matchesGenerated", { n: data.count }));
    load();
  };

  const deletePool = async (pool) => {
    try { await api.delete(`/pools/${pool.id}`); load(); }
    catch (e) { if (e.response?.status === 404) load(); else toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadDrawPdf = (pool) => {
    const nm = (a) => (a ? `${a.last_name} ${a.first_name}` : "—");
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(String(event.name || "KIHAP"), 14, 16);
    doc.setFontSize(13); doc.text(String(pool.name), 14, 26);
    let y = 34;
    if (pool.criteria) { doc.setFontSize(10); doc.setTextColor(120); doc.text(String(pool.criteria), 14, y); doc.setTextColor(0); y += 8; }
    const header = pool.bracket_type === "score" ? t("pool.execOrder") : t("pool.drawPreview");
    doc.setFontSize(11); doc.setFont(undefined, "bold"); doc.text(header, 14, y); doc.setFont(undefined, "normal"); y += 8;
    const line = (txt) => { if (y > 282) { doc.addPage(); y = 20; } doc.setFontSize(11); doc.text(txt, 14, y); y += 7; };
    if (pool.bracket_type === "score") {
      matches.filter((m) => m.pool_id === pool.id && m.athlete_id)
        .forEach((m, i) => line(`${i + 1}. ${nm(byId[m.athlete_id])}`));
    } else {
      matches.filter((m) => m.pool_id === pool.id && (m.red_athlete_id || m.blue_athlete_id) && (pool.bracket_type === "round_robin" || m.round === 1))
        .forEach((m, i) => {
          const bn = m.blue_athlete_id ? nm(byId[m.blue_athlete_id]) : t("pool.bye");
          line(`${i + 1}. ${nm(byId[m.red_athlete_id])}  vs  ${bn}`);
        });
    }
    doc.save(`${event.name || "KIHAP"}_${pool.name}.pdf`.replace(/[^\w.-]+/g, "_"));
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">{t("pool.title")}</h2>
          {disciplines.length > 1 && (
            <div className="flex rounded-md border border-border overflow-hidden">
              {disciplines.map((d) => (
                <button key={d} onClick={() => setDiscipline(d)} data-testid={`disc-tab-${d}`}
                  className={`px-3 py-1.5 text-sm font-medium ${discipline === d ? (d === "combat" ? "bg-hong text-white" : "bg-chung text-white") : "text-muted-foreground"}`}>
                  {d === "combat" ? t("type.combat") : t("type.poomsae")}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {canEdit && <Button variant="outline" onClick={() => { setAutoCfg((c) => ({ ...c, weight_tolerance_pct: 10, age_tolerance_pct: 100, height_tolerance_pct: 100, belt_tolerance_pct: discipline === "poomsae" ? 10 : 100, mixed_pools: false })); setAutoOpen(true); }} className="gap-2" data-testid="auto-gen-btn">
            <Sparkle weight="fill" className="text-gold" /> {t("pool.autoGen")}
          </Button>}
          {canEdit && <Button onClick={createPool} className="gap-2" data-testid="create-pool-btn"><Plus weight="bold" /> {t("pool.new")}</Button>}
        </div>
      </div>

      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent data-testid="auto-gen-dialog">
          <DialogHeader>
            <DialogTitle>{t("pool.autoTitle")}</DialogTitle>
            <DialogDescription>{t("pool.autoDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">{t("pool.method")}</Label>
              <div className="flex rounded-md border border-border overflow-hidden mt-1.5">
                {[["ai", t("pool.methodAi")], ["local", t("pool.methodLocal")]].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => setAutoCfg((c) => ({ ...c, method: v }))} data-testid={`auto-method-${v}`}
                    className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1.5 ${autoCfg.method === v ? "bg-gold text-black" : "text-muted-foreground"}`}>
                    {v === "ai" ? <Sparkle weight="fill" /> : <Cpu weight="fill" />} {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs" htmlFor="auto-min">{t("pool.minSize")}</Label>
                <Input id="auto-min" type="number" min={2} max={16} value={autoCfg.min_size} data-testid="auto-min-size"
                  onChange={(e) => setAutoCfg((c) => ({ ...c, min_size: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs" htmlFor="auto-max">{t("pool.maxSize")}</Label>
                <Input id="auto-max" type="number" min={2} max={16} value={autoCfg.max_size} data-testid="auto-max-size"
                  onChange={(e) => setAutoCfg((c) => ({ ...c, max_size: e.target.value }))} className="mt-1.5" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm" htmlFor="auto-mixed">{t("pool.mixedPools")}</Label>
                <div>
                  <Switch id="auto-mixed" checked={autoCfg.mixed_pools} data-testid="auto-mixed-switch"
                    onCheckedChange={(v) => setAutoCfg((c) => ({ ...c, mixed_pools: v }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {discipline === "combat" && (
                  <div>
                    <Label className="text-xs" htmlFor="auto-tol">{t("pool.weightTolShort")}</Label>
                    <Input id="auto-tol" type="number" min={1} max={100} value={autoCfg.weight_tolerance_pct} data-testid="auto-weight-tol"
                      onChange={(e) => setAutoCfg((c) => ({ ...c, weight_tolerance_pct: e.target.value }))} className="mt-1.5" />
                  </div>
                )}
                {discipline === "combat" && (
                  <div>
                    <Label className="text-xs" htmlFor="auto-height-tol">{t("pool.heightTolShort")}</Label>
                    <Input id="auto-height-tol" type="number" min={1} max={100} value={autoCfg.height_tolerance_pct} data-testid="auto-height-tol"
                      onChange={(e) => setAutoCfg((c) => ({ ...c, height_tolerance_pct: e.target.value }))} className="mt-1.5" />
                  </div>
                )}
                <div>
                  <Label className="text-xs" htmlFor="auto-belt-tol">{t("pool.beltTolShort")}</Label>
                  <Input id="auto-belt-tol" type="number" min={1} max={100} value={autoCfg.belt_tolerance_pct} data-testid="auto-belt-tol"
                    onChange={(e) => setAutoCfg((c) => ({ ...c, belt_tolerance_pct: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs" htmlFor="auto-age-tol">{t("pool.ageTolShort")}</Label>
                  <Input id="auto-age-tol" type="number" min={1} max={100} value={autoCfg.age_tolerance_pct} data-testid="auto-age-tol"
                    onChange={(e) => setAutoCfg((c) => ({ ...c, age_tolerance_pct: e.target.value }))} className="mt-1.5" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="auto-tol-hint">{t("pool.tolHint")}</p>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm" htmlFor="auto-replace">{t("pool.replace")}</Label>
              <Switch id="auto-replace" checked={autoCfg.replace} data-testid="auto-replace-switch"
                onCheckedChange={(v) => setAutoCfg((c) => ({ ...c, replace: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={runAutoGenerate} disabled={autoLoading} className="gap-2" data-testid="auto-generate-confirm">
              <Sparkle weight="fill" /> {autoLoading ? t("pool.generating") : t("pool.generate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {overridePool && (
        <Dialog open onOpenChange={(o) => !o && setOverridePool(null)}>
          <DialogContent data-testid="override-dialog">
            <DialogHeader>
              <DialogTitle>{t("pool.overrideTitle")} · {overridePool.name}</DialogTitle>
              <DialogDescription>{t("pool.overrideDesc")}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              {overridePool.discipline === "combat"
                ? [ovNum("num_rounds", t("set.rounds")), ovNum("round_duration_sec", t("set.roundDur")), ovNum("break_duration_sec", t("set.break")), ovNum("tech_ko_point_diff", t("set.koDiff")), ovNum("num_judges", t("set.judges")), ovFloat("consensus_window_sec", t("set.consensusWindow"))]
                : [ovNum("num_forms", t("set.forms")), ovNum("num_judges", t("set.judges"))]}
            </div>
            <p className="text-xs text-muted-foreground">{t("pool.overrideHint")}</p>
            <DialogFooter className="gap-2">
              {overridePool.config_override && <Button variant="ghost" onClick={() => saveOverride(true)} data-testid="override-reset">{t("pool.overrideReset")}</Button>}
              <Button onClick={() => saveOverride(false)} data-testid="override-save">{t("set.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Column id="unassigned" title={t("pool.unassigned")} subtitle={t("pool.athletes", { n: unassigned.length })} testid="pool-unassigned">
            {(() => {
              const byNm = (a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);
              const uF = unassigned.filter((a) => a.sex === "F").sort(byNm);
              const uM = unassigned.filter((a) => a.sex === "M").sort(byNm);
              const uO = unassigned.filter((a) => a.sex !== "F" && a.sex !== "M").sort(byNm);
              const GroupHead = ({ label, n, cls }) => (
                <div className={`px-2 py-1 rounded text-[10px] uppercase tracking-widest font-bold ${cls}`}>{label} ({n})</div>
              );
              return (
                <>
                  {uF.length > 0 && <GroupHead label={t("ath.female")} n={uF.length} cls="bg-pink-400 text-white" />}
                  {uF.map((a) => <AthleteCard key={a.id} a={a} />)}
                  {uM.length > 0 && <GroupHead label={t("ath.male")} n={uM.length} cls="bg-sky-400 text-white" />}
                  {uM.map((a) => <AthleteCard key={a.id} a={a} />)}
                  {uO.length > 0 && <GroupHead label={t("ath.noSex")} n={uO.length} cls="bg-secondary/60 text-muted-foreground" />}
                  {uO.map((a) => <AthleteCard key={a.id} a={a} />)}
                  {unassigned.length === 0 && <p className="text-xs text-muted-foreground">{t("pool.allAssigned")}</p>}
                </>
              );
            })()}
          </Column>

          {[...dPools].reverse().map((pool) => (
            <Column key={pool.id} id={pool.id} title={pool.name} subtitle={pool.criteria || t("pool.athletes", { n: pool.athlete_ids.length })} testid={`pool-col-${pool.id}`}
              headerRight={
                <div className="flex items-center gap-1">
                  {pool.config_override && <span className="text-[9px] uppercase tracking-wider text-brand-yellow font-bold mr-1" data-testid={`override-badge-${pool.id}`}>{t("pool.overrideActive")}</span>}
                  {canEdit && <button onClick={() => renamePool(pool)} className="text-muted-foreground hover:text-brand-yellow" title={t("pool.renamePrompt")} data-testid={`rename-pool-${pool.id}`}><PencilSimple size={16} /></button>}
                  {canEdit && <button onClick={() => openOverride(pool)} className="text-muted-foreground hover:text-brand-yellow" title={t("pool.overrideTitle")} data-testid={`override-pool-${pool.id}`}><GearSix size={16} /></button>}
                  {canEdit && <button onClick={() => deletePool(pool)} className="text-muted-foreground hover:text-hong" data-testid={`delete-pool-${pool.id}`}><Trash size={16} /></button>}
                </div>
              }>
              {pool.athlete_ids.map((id) => byId[id] && <AthleteCard key={id} a={byId[id]} />)}
              {pool.athlete_ids.length === 0 && <p className="text-xs text-muted-foreground">{t("pool.dragHere")}</p>}
              <div className="pt-2 flex flex-col items-start gap-2">
                {canEdit && <Select value={pool.bracket_type} onValueChange={(v) => setBracket(pool, v)}>
                  <SelectTrigger className="h-8 text-xs w-40" data-testid={`bracket-select-${pool.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{(pool.discipline === "poomsae" ? ["score", "single_elim", "round_robin"] : ["single_elim", "round_robin"]).map((k) => <SelectItem key={k} value={k}>{t(`bracket.${k}`)}</SelectItem>)}</SelectContent>
                </Select>}
                {canEdit && <Button size="sm" className="h-8 text-xs w-40" onClick={() => genMatches(pool)} data-testid={`gen-matches-${pool.id}`}>
                  {t("pool.genMatches")}
                </Button>}
                {matchCounts[pool.id] > 0 && <Badge className="text-[10px] bg-green-500/20 text-green-400 border-0">{t("pool.matches", { n: matchCounts[pool.id] })}</Badge>}
                {matchCounts[pool.id] > 0 && ["single_elim", "round_robin", "score"].includes(pool.bracket_type) && (
                  <div className="w-full rounded-md border border-border bg-secondary/30 p-2" data-testid={`draw-preview-${pool.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{pool.bracket_type === "score" ? t("pool.execOrder") : t("pool.drawPreview")}</div>
                      <button onClick={() => downloadDrawPdf(pool)} title={t("pool.downloadPdf")} data-testid={`download-pdf-${pool.id}`}
                        className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-brand-yellow transition-colors">
                        <DownloadSimple size={12} /> {t("common.pdf")}
                      </button>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {pool.bracket_type === "score"
                        ? matches.filter((m) => m.pool_id === pool.id && m.athlete_id).map((m, i) => (
                            <div key={m.id} className={`text-[11px] leading-tight whitespace-nowrap truncate ${i % 2 === 0 ? "text-hong" : "text-chung"}`} data-testid={`draw-match-${pool.id}-${i}`}>
                              {byId[m.athlete_id] ? `${byId[m.athlete_id].last_name} ${byId[m.athlete_id].first_name}` : "—"}
                            </div>
                          ))
                        : matches.filter((m) => m.pool_id === pool.id && (m.red_athlete_id || m.blue_athlete_id) && (pool.bracket_type === "round_robin" || m.round === 1)).map((m, i) => (
                            <div key={m.id} className="text-[11px] leading-tight whitespace-nowrap truncate" data-testid={`draw-match-${pool.id}-${i}`}>
                              <span className="text-hong">{byId[m.red_athlete_id] ? `${byId[m.red_athlete_id].last_name} ${byId[m.red_athlete_id].first_name}` : "—"}</span>
                              <span className="text-muted-foreground"> vs </span>
                              <span className="text-chung">{m.blue_athlete_id ? (byId[m.blue_athlete_id] ? `${byId[m.blue_athlete_id].last_name} ${byId[m.blue_athlete_id].first_name}` : "—") : t("pool.bye")}</span>
                            </div>
                          ))}
                    </div>
                    {canEdit && pool.bracket_type === "single_elim" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] w-full mt-2 gap-1.5" onClick={() => { if (window.confirm(t("res.reshuffleConfirm"))) genMatches(pool); }} data-testid={`reshuffle-pool-${pool.id}`}>
                        <ArrowsClockwise size={13} /> {t("res.reshuffle")}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Column>
          ))}
        </div>
      </DndContext>
    </div>
  );
}
